/**
 * <discount-code-offer>: click-to-copy discount code pill beside the PDP price.
 * Rendered server-side inside [data-render-price], so variant changes re-create it;
 * all state and listeners live on the element and are cleaned up on disconnect.
 * data-cutoff = epoch seconds (store timezone midnight after the end date); the element
 * removes itself once that moment passes, while the page stays open or after a tab resumes.
 */
(function () {
  'use strict';

  if (customElements.get('discount-code-offer')) return;

  const MAX_TIMEOUT = 2147483647;

  function fallbackCopy(text) {
    const field = document.createElement('textarea');
    const previousFocus = document.activeElement;
    field.value = text;
    field.setAttribute('readonly', '');
    field.setAttribute('aria-hidden', 'true');
    field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px;';
    document.body.appendChild(field);
    try {
      field.focus({ preventScroll: true });
      field.select();
      field.setSelectionRange(0, text.length);
      return document.execCommand('copy');
    } finally {
      field.remove();
      if (previousFocus && previousFocus.focus) previousFocus.focus({ preventScroll: true });
    }
  }

  class DiscountCodeOffer extends HTMLElement {
    connectedCallback() {
      this.cutoffMs = Number(this.dataset.cutoff) * 1000 || 0;
      if (this.checkExpired()) return;

      this.pill = this.querySelector('.dco__pill');
      this.status = this.querySelector('.dco__status');
      this.onClick = this.copy.bind(this);
      this.onVisibility = () => {
        if (document.visibilityState === 'visible') this.checkExpired();
      };
      if (this.pill) this.pill.addEventListener('click', this.onClick);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.scheduleExpiry();
    }

    disconnectedCallback() {
      clearTimeout(this.expiryTimer);
      clearTimeout(this.resetTimer);
      if (this.pill) this.pill.removeEventListener('click', this.onClick);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    checkExpired() {
      if (this.cutoffMs && Date.now() >= this.cutoffMs) {
        this.remove();
        return true;
      }
      return false;
    }

    scheduleExpiry() {
      if (!this.cutoffMs) return;
      const wait = Math.min(this.cutoffMs - Date.now(), MAX_TIMEOUT);
      // Long waits are re-scheduled until the real cutoff is reached.
      this.expiryTimer = setTimeout(() => {
        if (!this.checkExpired()) this.scheduleExpiry();
      }, wait);
    }

    async copy(event) {
      event.preventDefault();
      if (this.copying) return;
      this.copying = true;
      clearTimeout(this.resetTimer);
      this.status.textContent = '';

      const code = this.dataset.code;
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(code);
          copied = true;
        }
      } catch (error) {
        // Fall through to the execCommand fallback.
      }
      if (!copied) {
        try {
          copied = fallbackCopy(code);
        } catch (error) {
          copied = false;
        }
      }

      this.status.textContent = copied
        ? 'Code copied! Apply ' + code + ' at checkout.'
        : 'Could not copy automatically. Enter ' + code + ' at checkout.';
      this.copying = false;

      if (copied) {
        this.resetTimer = setTimeout(() => {
          this.status.textContent = '';
        }, 4000);
      }
    }
  }

  customElements.define('discount-code-offer', DiscountCodeOffer);
})();
